import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Editor from '@monaco-editor/react';
import { createUseStyles, Icon } from '@Sage/hoi-poi-ui';
import classnames from 'classnames';

import styles from './styles';

const useStyles = createUseStyles(styles, 'CodeEditor');

const CodeEditor = memo(
    ({
        className = '',
        language = 'javascript',
        onChange,
        readOnly = false,
        snippets,
        theme = 'vs-dark',
        value,
        ...props
    }) => {
        const classes = useStyles();
        const completionProviderRef = useRef(null);
        const containerRef = useRef(null);
        const [isFullscreen, setIsFullscreen] = useState(false);

        // ========================================
        // EVENT HANDLERS & CALLBACKS
        // ========================================
        const handleEditorDidMount = useCallback(
            (editor, monacoInstance) => {
                const hasSnippets = snippets && Object.keys(snippets).length > 0;

                if (!completionProviderRef.current && hasSnippets && language === 'javascript') {
                    completionProviderRef.current =
                        monacoInstance.languages.registerCompletionItemProvider('javascript', {
                            provideCompletionItems: (model, position) => {
                                const textUntilPosition = model.getValueInRange({
                                    endColumn: position.column,
                                    endLineNumber: position.lineNumber,
                                    startColumn: 1,
                                    startLineNumber: position.lineNumber,
                                });

                                const suggestions = [];
                                const suggestionKeys = new Set();

                                Object.keys(snippets).forEach((key) => {
                                    const prefix = key.slice(0, -1);
                                    const words = textUntilPosition.split(/\s+/);
                                    const lastWord = words[words.length - 1] || '';

                                    snippets[key].forEach((item) => {
                                        if (!suggestionKeys.has(item.fnName)) {
                                            const functionName = item.fnName.split('.').pop() || '';
                                            const shouldShow =
                                                textUntilPosition.includes(prefix + '.') ||
                                                textUntilPosition.endsWith(prefix) ||
                                                (lastWord.length > 0 &&
                                                    (prefix.startsWith(lastWord) ||
                                                        lastWord.startsWith(
                                                            prefix.substring(0, lastWord.length),
                                                        ) ||
                                                        functionName
                                                            .toLowerCase()
                                                            .startsWith(lastWord.toLowerCase())));

                                            if (shouldShow) {
                                                suggestionKeys.add(item.fnName);

                                                const insertText =
                                                    (item.snippetDoc || '') + item.snippet;
                                                const prefixIndex =
                                                    textUntilPosition.lastIndexOf(prefix);
                                                const lastWordIndex =
                                                    lastWord.length > 0
                                                        ? textUntilPosition.lastIndexOf(lastWord)
                                                        : -1;

                                                let startColumn = 1;
                                                if (prefixIndex >= 0) {
                                                    startColumn = prefixIndex + 1;
                                                } else if (
                                                    lastWordIndex >= 0 &&
                                                    (prefix.startsWith(lastWord) ||
                                                        functionName
                                                            .toLowerCase()
                                                            .startsWith(lastWord.toLowerCase()))
                                                ) {
                                                    startColumn = lastWordIndex + 1;
                                                }

                                                const range = {
                                                    endColumn: position.column,
                                                    endLineNumber: position.lineNumber,
                                                    startColumn,
                                                    startLineNumber: position.lineNumber,
                                                };

                                                const suggestion = {
                                                    detail: 'SSM Snippet',
                                                    documentation: item.snippetDoc
                                                        ? {
                                                              isTrusted: true,
                                                              value: item.snippetDoc,
                                                          }
                                                        : undefined,
                                                    filterText: item.fnName.toLowerCase(),
                                                    insertText,
                                                    insertTextRules:
                                                        monacoInstance.languages
                                                            .CompletionItemInsertTextRule
                                                            .InsertAsSnippet,
                                                    kind: monacoInstance.languages
                                                        .CompletionItemKind.Function,
                                                    label: {
                                                        description: 'SSM snippet',
                                                        label: item.fnName,
                                                    },
                                                    range,
                                                    sortText: '0000' + item.fnName,
                                                };

                                                suggestions.push(suggestion);
                                            }
                                        }
                                    });
                                });

                                return { suggestions };
                            },
                            triggerCharacters: ['.'],
                        });
                }
            },
            [language, snippets],
        );

        const handleEditorChange = useCallback(
            (newValue) => {
                onChange?.(newValue);
            },
            [onChange],
        );

        const toggleFullscreen = useCallback(() => {
            if (!containerRef.current) return;

            if (!document.fullscreenElement) {
                containerRef.current.requestFullscreen().catch((err) => {
                    console.error('Error attempting to enable fullscreen:', err);
                });
            } else {
                document.exitFullscreen();
            }
        }, []);

        // ========================================
        // DERIVED STATE & MEMOIZATION
        // ========================================
        const editorOptions = useMemo(
            () => ({
                acceptSuggestionOnCommitCharacter: true,
                acceptSuggestionOnEnter: 'on',
                automaticLayout: true,
                folding: true,
                fontSize: 14,
                lineNumbers: 'on',
                minimap: { enabled: false },
                quickSuggestions: readOnly
                    ? false
                    : {
                          comments: false,
                          other: true,
                          strings: true,
                      },
                readOnly: !!readOnly,
                renderLineHighlight: 'all',
                scrollBeyondLastLine: false,
                suggest: {
                    showKeywords: true,
                    showSnippets: true,
                },
                suggestOnTriggerCharacters: !readOnly,
                tabSize: 2,
                wordWrap: 'on',
            }),
            [readOnly],
        );

        // ========================================
        // EFFECTS
        // ========================================
        useEffect(() => {
            const handleFullscreenChange = () => {
                setIsFullscreen(!!document.fullscreenElement);
            };

            document.addEventListener('fullscreenchange', handleFullscreenChange);
            return () => {
                document.removeEventListener('fullscreenchange', handleFullscreenChange);
            };
        }, []);

        useEffect(() => {
            return () => {
                if (completionProviderRef.current) {
                    completionProviderRef.current.dispose();
                }
            };
        }, []);

        // ========================================
        // RENDER
        // ========================================
        return (
            <div
                ref={containerRef}
                className={classnames(
                    classes.root,
                    { [classes.fullscreen]: isFullscreen },
                    className,
                )}
            >
                <Icon
                    className={classes.fullscreenBtn}
                    name={isFullscreen ? 'fullscreenExit' : 'fullscreen'}
                    onClick={toggleFullscreen}
                />
                <Editor
                    defaultLanguage={language}
                    height="100%"
                    onChange={handleEditorChange}
                    onMount={handleEditorDidMount}
                    options={editorOptions}
                    theme={theme}
                    value={value}
                    {...props}
                />
            </div>
        );
    },
);

CodeEditor.propTypes = {
    className: PropTypes.string,
    language: PropTypes.string,
    onChange: PropTypes.func,
    readOnly: PropTypes.bool,
    snippets: PropTypes.object,
    theme: PropTypes.string,
    value: PropTypes.string,
};

export default CodeEditor;
